import { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  TextField,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { CmrFieldMapping, CmrFieldDefinition } from '../../types';

const SOURCE_TYPE_LABELS: Record<string, string> = {
  CARGO: 'Ordre',
  ORGANIZATION: 'Organisation',
  STATIC: 'Statisk tekst',
  EMPTY: 'Tom',
};

export default function CmrSettings() {
  const [mappings, setMappings] = useState<CmrFieldMapping[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data: fieldsData, isLoading: fieldsLoading } = useQuery({
    queryKey: ['cmr-fields'],
    queryFn: () => api.getCmrFields(),
  });

  const { data: savedMappings, isLoading: mappingsLoading } = useQuery({
    queryKey: ['cmr-mappings'],
    queryFn: () => api.getCmrMappings(),
  });

  useEffect(() => {
    if (savedMappings) {
      setMappings(savedMappings);
    }
  }, [savedMappings]);

  const saveMutation = useMutation({
    mutationFn: () => api.updateCmrMappings(mappings),
    onSuccess: () => {
      setHasChanges(false);
      setSaveMessage({ type: 'success', text: 'CMR-mappings gemt!' });
      setTimeout(() => setSaveMessage(null), 3000);
    },
    onError: () => {
      setSaveMessage({ type: 'error', text: 'Kunne ikke gemme CMR-mappings' });
    },
  });

  const handleSourceTypeChange = (fieldNumber: number, sourceType: string) => {
    setMappings((prev) =>
      prev.map((m) =>
        m.fieldNumber === fieldNumber
          ? { ...m, sourceType: sourceType as CmrFieldMapping['sourceType'], sourceField: null, staticText: null }
          : m,
      ),
    );
    setHasChanges(true);
  };

  const handleSourceFieldChange = (fieldNumber: number, sourceField: string) => {
    setMappings((prev) =>
      prev.map((m) =>
        m.fieldNumber === fieldNumber ? { ...m, sourceField } : m,
      ),
    );
    setHasChanges(true);
  };

  const handleStaticTextChange = (fieldNumber: number, staticText: string) => {
    setMappings((prev) =>
      prev.map((m) =>
        m.fieldNumber === fieldNumber ? { ...m, staticText } : m,
      ),
    );
    setHasChanges(true);
  };

  if (fieldsLoading || mappingsLoading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!fieldsData) return null;

  const { fields, availableFields } = fieldsData;

  const getFieldDef = (fieldNumber: number): CmrFieldDefinition | undefined =>
    fields.find((f) => f.fieldNumber === fieldNumber);

  const getMapping = (fieldNumber: number): CmrFieldMapping =>
    mappings.find((m) => m.fieldNumber === fieldNumber) || {
      fieldNumber,
      sourceType: 'EMPTY',
      sourceField: null,
      staticText: null,
    };

  return (
    <Box>
      {saveMessage && (
        <Alert severity={saveMessage.type} sx={{ mb: 1.5 }}>
          {saveMessage.text}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }}>#</TableCell>
              <TableCell sx={{ width: 200 }}>Felt</TableCell>
              <TableCell sx={{ width: 160 }}>Kildetype</TableCell>
              <TableCell>Kildefelt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: 24 }, (_, i) => i + 1).map((fieldNum) => {
              const fieldDef = getFieldDef(fieldNum);
              const mapping = getMapping(fieldNum);
              const sourceFields =
                mapping.sourceType === 'CARGO'
                  ? availableFields.CARGO
                  : mapping.sourceType === 'ORGANIZATION'
                    ? availableFields.ORGANIZATION
                    : [];

              return (
                <TableRow key={fieldNum} hover>
                  <TableCell>
                    <Chip label={fieldNum} size="small" color="default" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {fieldDef?.name || `Felt ${fieldNum}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fieldDef?.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={mapping.sourceType}
                      onChange={(e) => handleSourceTypeChange(fieldNum, e.target.value)}
                      fullWidth
                      sx={{ '& .MuiSelect-select': { py: 0.5 } }}
                    >
                      {Object.entries(SOURCE_TYPE_LABELS).map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    {(mapping.sourceType === 'CARGO' || mapping.sourceType === 'ORGANIZATION') && (
                      <Select
                        size="small"
                        value={mapping.sourceField || ''}
                        onChange={(e) => handleSourceFieldChange(fieldNum, e.target.value)}
                        fullWidth
                        displayEmpty
                        sx={{ '& .MuiSelect-select': { py: 0.5 } }}
                      >
                        <MenuItem value="">
                          <em>Vælg felt...</em>
                        </MenuItem>
                        {sourceFields.map((f) => (
                          <MenuItem key={f.key} value={f.key}>
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                    {mapping.sourceType === 'STATIC' && (
                      <TextField
                        size="small"
                        value={mapping.staticText || ''}
                        onChange={(e) => handleStaticTextChange(fieldNum, e.target.value)}
                        placeholder="Indtast tekst..."
                        fullWidth
                        sx={{ '& .MuiInputBase-input': { py: 0.5, px: 1 } }}
                      />
                    )}
                    {mapping.sourceType === 'EMPTY' && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Feltet udfyldes ikke
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {hasChanges && (
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={saveMutation.isPending ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Gemmer...' : 'Gem CMR-mappings'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
